/**
 * NIGHTBOUND - ephemeral toasts.
 *
 * Watches the shelter log and surfaces the newest entry as a short-lived toast.
 * Errors are *not* toasts: they persist in an ErrorBanner until dismissed,
 * because a player should never lose the reason something failed.
 */

import { useEffect, useRef, useState } from 'react'
import { useGame } from '../state/GameProvider'
import { cx } from './ui'

interface Toast {
  id: number
  message: string
  detail?: string
  tone: 'neutral' | 'good' | 'bad'
}

const TONE_BORDER: Record<Toast['tone'], string> = {
  neutral: 'border-ash-600/70',
  good: 'border-rot-600/70',
  bad: 'border-blood-500/70',
}

let toastSeq = 0

export function ToastStack() {
  const { log } = useGame()
  const [toasts, setToasts] = useState<Toast[]>([])
  const seen = useRef<number | null>(null)

  useEffect(() => {
    const newest = log[0]
    if (!newest || newest.id === seen.current) return
    seen.current = newest.id
    const toast: Toast = {
      id: ++toastSeq,
      message: newest.message,
      tone: newest.tone,
      ...(newest.detail ? { detail: newest.detail } : {}),
    }
    setToasts((current) => [...current, toast].slice(-4))
    const timer = setTimeout(() => {
      setToasts((current) => current.filter((item) => item.id !== toast.id))
    }, 5200)
    return () => clearTimeout(timer)
  }, [log])

  const dismiss = (id: number) => setToasts((current) => current.filter((item) => item.id !== id))

  if (toasts.length === 0) return null

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed inset-x-0 bottom-20 z-40 flex flex-col items-center gap-2 px-3 lg:bottom-5 lg:left-auto lg:right-5 lg:items-end"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cx(
            'nb-toast-in pointer-events-auto flex w-full max-w-sm items-start gap-2 rounded-[4px] border bg-night-900/95 px-3 py-2 shadow-[0_14px_30px_-16px_rgba(0,0,0,0.95)] backdrop-blur',
            TONE_BORDER[toast.tone],
          )}
          role="status"
        >
          <div className="min-w-0 flex-1">
            <p className="text-xs leading-snug text-bone-200">{toast.message}</p>
            {toast.detail ? (
              <p className="mt-0.5 text-[11px] leading-snug text-ash-400">{toast.detail}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => dismiss(toast.id)}
            aria-label="Dismiss"
            className="-mr-1 -mt-1 shrink-0 rounded px-1.5 text-ash-500 hover:text-bone-200"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  )
}

export function ErrorBanner() {
  const { error, dismissError } = useGame()
  if (!error) return null
  return (
    <div
      role="alert"
      className="pointer-events-auto fixed inset-x-0 top-0 z-50 flex justify-center px-3 pt-3"
    >
      <div className="nb-emerge flex w-full max-w-lg items-start gap-3 rounded-[4px] border border-blood-500/70 bg-blood-950/95 px-3 py-2 shadow-[0_14px_30px_-14px_rgba(0,0,0,0.95)] backdrop-blur">
        <span aria-hidden className="mt-0.5 text-blood-400">
          ✕
        </span>
        <p className="flex-1 text-xs leading-snug text-bone-200">{error}</p>
        <button
          type="button"
          onClick={dismissError}
          aria-label="Dismiss"
          className="-mr-1 -mt-1 shrink-0 rounded px-1.5 text-blood-400 hover:text-bone-200"
        >
          ×
        </button>
      </div>
    </div>
  )
}
